import React, { useEffect, useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { Calendar, User, ArrowRight, ArrowLeft, Tag } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BlogList = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('Tümü');

  useEffect(() => {
    const fetchPosts = async () => {
      try {
        await axios.post(`${API}/seed`);
        const response = await axios.get(`${API}/blog`);
        setPosts(response.data);
      } catch (error) {
        console.error('Error fetching blog posts:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchPosts();
  }, []);

  const categories = ['Tümü', ...new Set(posts.map(post => post.category))];

  const filteredPosts = selectedCategory === 'Tümü' 
    ? posts 
    : posts.filter(post => post.category === selectedCategory);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('tr-TR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="pt-20 min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#2A9D8F]"></div>
      </div>
    );
  }

  return (
    <div data-testid="blog-page" className="pt-20">
      {/* Hero Section */}
      <section className="section-spacing bg-gradient-to-br from-[#2A9D8F]/5 to-[#E76F51]/5">
        <div className="section-container">
          <div className="text-center max-w-3xl mx-auto">
            <span className="inline-block px-4 py-2 bg-[#2A9D8F]/10 text-[#2A9D8F] rounded-full text-sm font-medium mb-6">
              Blog & Haberler
            </span>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-[#264653] mb-6 font-['Outfit']">
              Güncel Yazılarımız
            </h1>
            <p className="text-lg text-[#264653]/70 leading-relaxed">
              İnsan kaynakları, personel yönetimi ve sektörel gelişmeler hakkında 
              bilgilendirici içeriklerimizi takip edin.
            </p>
          </div>
        </div>
      </section>

      {/* Category Filter */}
      <section className="py-8 border-b border-[#264653]/10">
        <div className="section-container">
          <div className="flex flex-wrap justify-center gap-3">
            {categories.map((category) => (
              <button
                key={category}
                onClick={() => setSelectedCategory(category)}
                className={`px-5 py-2 rounded-full text-sm font-medium transition-colors ${
                  selectedCategory === category
                    ? 'bg-[#2A9D8F] text-white'
                    : 'bg-[#264653]/5 text-[#264653] hover:bg-[#264653]/10'
                }`}
                data-testid={`category-${category}`}
              >
                {category}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Blog Posts Grid */}
      <section className="section-spacing">
        <div className="section-container">
          {filteredPosts.length > 0 ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filteredPosts.map((post) => (
                <article
                  key={post.id}
                  className="blog-card group"
                  data-testid={`blog-card-${post.slug}`}
                >
                  <div className="relative overflow-hidden">
                    <img
                      src={post.image_url || 'https://images.pexels.com/photos/3184465/pexels-photo-3184465.jpeg'}
                      alt={post.title}
                      className="w-full h-48 object-cover transition-transform duration-300 group-hover:scale-105"
                    />
                    <div className="absolute top-4 left-4">
                      <span className="px-3 py-1 bg-[#2A9D8F] text-white text-xs font-medium rounded-full">
                        {post.category}
                      </span>
                    </div>
                  </div>
                  <div className="p-6">
                    <div className="flex items-center gap-4 text-sm text-[#264653]/60 mb-3">
                      <span className="flex items-center gap-1">
                        <Calendar size={14} />
                        {formatDate(post.created_at)}
                      </span>
                      <span className="flex items-center gap-1">
                        <User size={14} />
                        {post.author}
                      </span>
                    </div>
                    <h2 className="text-xl font-semibold text-[#264653] mb-3 font-['Outfit'] group-hover:text-[#2A9D8F] transition-colors">
                      {post.title}
                    </h2>
                    <p className="text-[#264653]/70 text-sm leading-relaxed mb-4 line-clamp-2">
                      {post.excerpt}
                    </p>
                    <Link
                      to={`/blog/${post.slug}`}
                      className="inline-flex items-center gap-2 text-[#2A9D8F] font-medium text-sm group-hover:gap-3 transition-all"
                    >
                      Devamını Oku <ArrowRight size={16} />
                    </Link>
                  </div>
                </article>
              ))}
            </div>
          ) : (
            <div className="text-center py-16">
              <p className="text-[#264653]/60 text-lg">Bu kategoride henüz yazı bulunmuyor.</p>
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

const BlogDetail = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchPost = async () => {
      try {
        const response = await axios.get(`${API}/blog/${slug}`);
        setPost(response.data);
      } catch (error) {
        console.error('Error fetching blog post:', error);
        navigate('/blog');
      } finally {
        setLoading(false);
      }
    };
    fetchPost();
  }, [slug, navigate]);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('tr-TR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="pt-20 min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#2A9D8F]"></div>
      </div>
    );
  }

  if (!post) {
    return null;
  }

  return (
    <div data-testid="blog-detail-page" className="pt-20">
      {/* Hero */}
      <section className="relative py-24 bg-gradient-to-br from-[#2A9D8F]/5 to-[#E76F51]/5">
        <div className="section-container">
          <button
            onClick={() => navigate('/blog')}
            className="flex items-center gap-2 text-[#264653]/70 hover:text-[#2A9D8F] mb-8 transition-colors"
            data-testid="back-to-blog"
          >
            <ArrowLeft size={18} /> Blog'a Dön
          </button>
          <div className="max-w-3xl">
            <span className="inline-flex items-center gap-2 px-4 py-2 bg-[#2A9D8F]/10 text-[#2A9D8F] rounded-full text-sm font-medium mb-6">
              <Tag size={14} /> {post.category}
            </span>
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-[#264653] mb-6 font-['Outfit']">
              {post.title}
            </h1>
            <div className="flex items-center gap-6 text-[#264653]/60">
              <span className="flex items-center gap-2">
                <User size={18} />
                {post.author}
              </span>
              <span className="flex items-center gap-2">
                <Calendar size={18} />
                {formatDate(post.created_at)}
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="section-spacing">
        <div className="section-container">
          <div className="grid lg:grid-cols-3 gap-12">
            <div className="lg:col-span-2">
              {post.image_url && (
                <img
                  src={post.image_url}
                  alt={post.title}
                  className="w-full h-[400px] object-cover rounded-3xl mb-8"
                />
              )}
              <div
                className="prose prose-lg max-w-none text-[#264653]/80"
                dangerouslySetInnerHTML={{ __html: post.content }}
              />
            </div>

            <aside className="lg:col-span-1">
              <div className="sticky top-24 space-y-8">
                <div className="p-6 rounded-2xl bg-[#2A9D8F]/5 border border-[#2A9D8F]/10">
                  <h3 className="font-semibold text-[#264653] mb-4 font-['Outfit']">
                    Yardıma mı ihtiyacınız var?
                  </h3>
                  <p className="text-[#264653]/70 text-sm mb-4">
                    İnsan kaynakları ihtiyaçlarınız için uzman ekibimizle iletişime geçin.
                  </p>
                  <Link
                    to="/iletisim"
                    className="btn-primary w-full flex justify-center items-center gap-2"
                  >
                    İletişime Geç <ArrowRight size={16} />
                  </Link>
                </div>

                <div className="p-6 rounded-2xl bg-white border border-[#264653]/5">
                  <h3 className="font-semibold text-[#264653] mb-4 font-['Outfit']">
                    Paylaş
                  </h3>
                  <div className="flex gap-3">
                    <a
                      href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(post.title)}&url=${encodeURIComponent(window.location.href)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-10 h-10 rounded-full bg-[#264653]/5 flex items-center justify-center hover:bg-[#2A9D8F] hover:text-white transition-colors"
                    >
                      𝕏
                    </a>
                    <a
                      href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(window.location.href)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-10 h-10 rounded-full bg-[#264653]/5 flex items-center justify-center hover:bg-[#2A9D8F] hover:text-white transition-colors"
                    >
                      in
                    </a>
                  </div>
                </div>
              </div>
            </aside>
          </div>
        </div>
      </section>
    </div>
  );
};

const Blog = () => {
  const { slug } = useParams();
  
  if (slug) {
    return <BlogDetail />;
  }
  
  return <BlogList />;
};

export default Blog;
